import type { ColumnType } from '@deckspace/types';

/**
 * UI-layer definition for a field type.
 * Display and interactivity concerns — no data validation, no Zod.
 */
export interface FieldTypeUI {
  /** Human-readable label for UI dropdowns. */
  label: string;
  /**
   * Format a value for display. Single function — no showAs split.
   * Deckspace's equivalent of cell-level showAs is component-level:
   * Claude picks DataTable vs. bar chart vs. kanban. If cell-level visual
   * modes (progress bars, sparklines) are needed, add showAs here.
   */
  format: (value: unknown, opts?: Record<string, unknown>) => string;
  /** Whether this type supports inline editing. */
  editable: boolean;
  /** Whether this type represents a numeric value (for input mode, sorting, etc.) */
  isNumeric: boolean;
  /**
   * Future: parse(input: string, opts?) → T | null
   * Inverse of format — parses user input back to a typed cell value.
   * Not needed until inline cell editing ships. Add here when it does.
   */
}

/**
 * Registry of all field type UI definitions, keyed by ColumnType.
 * This is the single source of truth for display-layer type behavior.
 */
export const FIELD_TYPE_UI_REGISTRY: Record<ColumnType, FieldTypeUI> = {
  text: {
    label: 'Text',
    format: (value: unknown) => {
      if (value === null || value === undefined) return '';
      return String(value);
    },
    editable: true,
    isNumeric: false,
  },
  number: {
    label: 'Number',
    format: (value: unknown, opts?: Record<string, unknown>) => {
      if (value === null || value === undefined) return '';
      const unit = opts?.unit as string | undefined;
      if (unit && typeof value === 'number') return `${value} ${unit}`;
      return String(value);
    },
    editable: true,
    isNumeric: true,
  },
  percent: {
    label: 'Percent',
    format: (value: unknown) => {
      if (value === null || value === undefined) return '';
      // Convention: percent values are stored as 0-1 ratios (matches Teable).
      // Formatter multiplies by 100 at display time. Both chart axes and
      // DataTable cells consume this same path, so they stay in lockstep.
      if (typeof value === 'number') return `${(value * 100).toFixed(2)}%`;
      return String(value);
    },
    editable: true,
    isNumeric: true,
  },
  currency: {
    label: 'Currency',
    format: (value: unknown, opts?: Record<string, unknown>) => {
      if (value === null || value === undefined) return '';
      if (typeof value === 'number') {
        const currencyCode = (opts?.currency as string) ?? 'USD';
        try {
          return value.toLocaleString('en-US', {
            style: 'currency',
            currency: currencyCode,
          });
        } catch {
          return `${currencyCode === 'USD' ? '$' : currencyCode}${value.toLocaleString()}`;
        }
      }
      return String(value);
    },
    editable: true,
    isNumeric: true,
  },
  date: {
    label: 'Date',
    format: (value: unknown) => {
      if (value === null || value === undefined) return '';
      return String(value);
    },
    editable: true,
    isNumeric: false,
  },
  boolean: {
    label: 'Boolean',
    format: (value: unknown) => {
      if (value === null || value === undefined) return '';
      return String(value);
    },
    editable: true,
    isNumeric: false,
  },
  url: {
    label: 'URL',
    format: (value: unknown) => {
      if (value === null || value === undefined) return '';
      return String(value);
    },
    editable: true,
    isNumeric: false,
  },
};

/** Look up the UI-layer definition for a field type. */
export function getFieldTypeUI(type: ColumnType): FieldTypeUI {
  return FIELD_TYPE_UI_REGISTRY[type];
}
